import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LayoutConfigService } from '../../services/layout-config.service';
import { environment } from '../../environments/environment';
import { AuthDataService } from 'src/data-store/auth-data.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import {
  AccountRoute, AuthRoute,
  CollaboratorRoute, DashboardRoute, InvestmentsListRoute, MoneyAndTransactionsRoute, NewInvestmentRoute, PersonalInformationRoute, ProductsRoute
} from 'src/models/routes.model';
import { UserDataService } from 'src/data-store/user-data.service';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { HotlineAvailabilityService } from 'src/services/hotline-availability.service';
import { ENativeBottomNavbarItem, IHotlineAvailabilityResult } from 'src/models/app.model';
import { DeviceService } from 'src/services/device.service';
import * as CookieConsent from 'vanilla-cookieconsent';
import { UserNotificationService } from 'src/services/user-notification.service';
import { NativeSafeZoneService } from 'src/services/native-safe-zone.service';
import { STYLE_VALUES_PASSED_VIA_HTML } from 'src/util/constants';

@Component({
  standalone: false,
  templateUrl: './basic-layout.component.html',
  styleUrl: './basic-layout.component.less'
})
export class BasicLayoutComponent {
  private layoutConfigService = inject(LayoutConfigService);
  private authDataService = inject(AuthDataService);
  private userDataService = inject(UserDataService);
  private hotlineAvailabilityService = inject(HotlineAvailabilityService);
  private userNotificationService = inject(UserNotificationService);

  private router = inject(Router);
  public deviceService = inject(DeviceService);
  public nativeSafeZoneService = inject(NativeSafeZoneService);

  private get routeData() {
    return this.layoutConfigService.basicLayoutConfig;
  }
  private userAndTokenExist = computed<boolean>(() => !!this.userDataService.loggedUser() && !!this.authDataService.accessToken());
  private currentUserLoading = computed<boolean>(() => this.userDataService.userIsLoading(ELoadingEntity.CURRENT_USER));

  public authenticatedHeader = computed<boolean>(() => this.routeData()?.authenticatedHeader ?? false);
  public showFooter = computed<boolean>(() => (this.routeData()?.showFooter) ?? false);
  private bottomSpacing = computed<boolean>(() => this.routeData()?.bottomSpacing ?? true);
  public onlyLogoHeader = computed<boolean>(() => this.routeData()?.onlyLogoHeader ?? false);
  public showMobileAppFooter = computed<boolean>(() => (this.deviceService.isNativePlatform && (this.routeData()?.showMobileAppFooter ?? true)) ?? false);
  // TODO: https://gitlab.com/dfcz/bwm-frontend/-/work_items/126
  // TODO: https://gitlab.com/dfcz/bwm-frontend/-/merge_requests/311#note_3324156209
  public layoutBottomPadding = computed<string | number>(() =>
    (this.showFooter() ?
    !this.isLoginUrl() && this.deviceService.isNativePlatform ?
    this.bottomNavbarHeight :
    `var(${STYLE_VALUES_PASSED_VIA_HTML.ANDROID_NAVIGATION_HEIGHT})` :
    this.bottomSpacing() ?
    this.bottomNavbarHeight :
    0)
  );
  private get bottomNavbarHeight() {
    return `var(${this.isInvestmentDetail()
      ? STYLE_VALUES_PASSED_VIA_HTML.INVESTMENT_DETAIL_BOTTOM_STICKY_ACTIONS
      : STYLE_VALUES_PASSED_VIA_HTML.BOTTOM_NAVBAR_HEIGHT})`;
  }
  public showHeaderHamburger = computed<boolean>(() => this.routeData()?.showHeaderHamburger ?? false);
  public showHeaderLinks = computed<boolean>(() => this.routeData()?.showHeaderLinks ?? false);
  public showFooterLinks = computed<boolean>(() => this.routeData()?.showFooterLinks ?? false);
  public previewOnlyHeaderText = computed<boolean>(() => this.routeData()?.previewOnlyHeaderText ?? false);
  public headerBackground = computed<'transparent' | 'filled'>(() => this.routeData()?.headerBackground ?? 'filled');
  // https://gitlab.com/dfcz/bwm-frontend/-/issues/65
  // todo: make dynamic and consider - first login flow, coming to login page when already authed, native apps issue with route resolve, refresh token failure support
  public showLoginLoader = signal<boolean>(false);

  public links = signal<{ android: string; ios: string; facebook: string; linkedin: string; }>({
    android: environment.appLinkAndroid ?? '',
    ios: environment.appLinkIphone ?? '',
    facebook: environment.facebookLink ?? '',
    linkedin: environment.linkedinLink ?? '',
  });

  public hotlineAvailability = computed<IHotlineAvailabilityResult | null>(() => this.hotlineAvailabilityService.hotlineAvailability());

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  public isLoginUrl = computed(() => this.currentUrl() === `/${AuthRoute.LOGIN}`);
  public isInvestmentDetail = computed(() => this.currentUrl().includes(ProductsRoute.INVESTMENT_DETAIL));

  public activeNativeBottomNavbarItem = computed<ENativeBottomNavbarItem | null>(() => {
    if (!this.deviceService.isNativePlatform) return null;

    const map: Record<ENativeBottomNavbarItem, string[]> = {
      [ENativeBottomNavbarItem.SUMMARY]: [DashboardRoute.DASHBOARD],
      [ENativeBottomNavbarItem.INVESTMENTS]: [
        NewInvestmentRoute.NEW_INVESTMENT,
        ProductsRoute.INVESTMENT_DETAIL,
        InvestmentsListRoute.INVESTMENTS,
      ],
      [ENativeBottomNavbarItem.MONEY]: [MoneyAndTransactionsRoute.MONEY_TRANSACTIONS],
      [ENativeBottomNavbarItem.MY_CONSULTANT]: [CollaboratorRoute.COLLABORATOR],
      [ENativeBottomNavbarItem.MY_ACCOUNT]: [AccountRoute.ACCOUNT, PersonalInformationRoute.PERSONAL_INFORMATION],
    };

    const url = this.currentUrl();

    for (const [item, routes] of Object.entries(map)) {
      if (routes.some(r => url.includes(r))) {
        return Number(item) as ENativeBottomNavbarItem;
      }
    }

    return null;
  });

  public displayCookieConsentBar(): void {
    CookieConsent.show(true);
  }
}
